/**
 * IdleReleaseWarning — prévient le chatteur AVANT que ses discussions soient
 * libérées (11-10-2026, demande de Kayla).
 *
 * Ses discussions tombent toutes ensemble quand il n'a écrit à AUCUN fan
 * depuis 15 min (cf `WRITE_IDLE_MS`, conversationLocks.ts). Après 10 min sans
 * écrire, ce bandeau s'affiche avec le compte à rebours ; la dernière minute,
 * il passe au rouge. Il disparaît dès qu'il écrit à quelqu'un (le chrono est
 * réactif) ou que ses discussions sont libérées (il ne tient plus rien).
 *
 * Rien pour les superviseurs (ils ne verrouillent jamais) ni pour un chatteur
 * qui ne tient aucune discussion.
 *
 * 🎨 Nouveau thème (02-11-2026) : c'est le `Bandeau` de la librairie — une
 * bande d'état d'un bord à l'autre, ambre pendant l'avertissement, rouge la
 * dernière minute (la couleur dit le SENS, le texte reste blanc). Le compte à
 * rebours bat sur l'horloge de la famille Temps (`Temps.useMaintenant`, une
 * minuterie par rythme pour toute la page), plus sur un minuteur écrit ici à la main.
 */
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";

import { api } from "@/../convex/_generated/api";
import { Bandeau, Temps } from "@/shared/ui";

import { formatCountdown, idleWarningFor } from "./lockLogic";
import { useConversationLocks } from "./useConversationLocks";

export function IdleReleaseWarning() {
  const { accountId, isBypass, myWorkosUserId, locksByConv } =
    useConversationLocks();

  let holdsAny = false;
  for (const lock of locksByConv.values()) {
    if (lock.lockedByWorkosUserId === myWorkosUserId) {
      holdsAny = true;
      break;
    }
  }
  const watching = !!accountId && !isBypass && holdsAny;

  const clock = useQuery(
    api.conversationLocks.myWriteClock,
    watching && accountId ? { accountId } : "skip",
  );

  if (!watching) return null;
  // L'horloge à la seconde ne tourne QUE tant qu'il y a quelque chose à
  // surveiller : elle vit dans le sous-composant, monté seulement ici.
  return <Avertissement lastWrittenAt={clock?.lastWrittenAt} />;
}

function Avertissement({
  lastWrittenAt,
}: {
  lastWrittenAt: number | null | undefined;
}) {
  const { t } = useTranslation();
  const now = Temps.useMaintenant(1000);
  const warning = idleWarningFor(lastWrittenAt, now);
  if (!warning) return null;

  const lastCall = warning.stage === "lastCall";
  return (
    <Bandeau
      ton={lastCall ? "erreur" : "attention"}
      texte={t(lastCall ? "idleRelease.lastCall" : "idleRelease.warning", {
        countdown: formatCountdown(warning.msLeft),
      })}
    />
  );
}
